<template>
  <div class="container--fluid kanban-page-root">
    <div
      class="kanban-blurred-zone"
      :class="{ 'kanban-blurred-zone--blurred': isExpired && !hideTariffOverlay }"
    >
      <VRow class="kanban-header">
        <VCol
          class="py-0"
          cols="12"
          md="8"
          lg="9"
        >
          <VacancyCandidatesKanbanActions
            v-if="isCreateButtonsAvailable"
            :candidates="candidates"
            :is-kanban-data-ready="isKanbanDataReady"
            :show-hidden="showHidden"
            :is-show-all-stages="isShowAllStages"
            @showHiddenStages="showHiddenStages"
            @showAllStages="showAllStages"
            @addStage="addStage"
          />
        </VCol>
        <VCol
          class="py-0"
          cols="12"
          md="6"
          lg="3"
        >
          <VacancyCandidatesKanbanSearch v-model="search" />
        </VCol>
      </VRow>
      <div class="kanban-content-wrapper">
      <Preloader v-if="loading || !vacancy" />
      <VAlert v-else-if="candidates.length === 0 && !search && !searchLoading && !loading" type="info">
        {{ $t('noCandidates') }}
      </VAlert>

      <div
        class="kanban-container"
        ref="kanbanContainer"
        :style="{ ...cssVars, opacity: loading ? '0' : '1', position: 'relative' }"
      >
      <Preloader v-if="searchLoading" class="search-loader" />
      <template>
        <div class="kanban-scope">
          <kanban-board
            v-if="isKanbanDataReady"
            :stages="stagesCodes"
            :blocks="filteredCandidates"
            :config="kanbanConfig"
            @update-block="updateBlock"
          >
            <div
              v-for="(count, stageStatus) in stagesCounted"
              :slot="stageStatus"
              :key="stageStatus"
              class="drag-header-text grey--text text--darken-3 drag-enabled"
            >
              <VacancyCandidatesKanbanStage
                :stage="stagesMap[stageStatus]"
                @delete="deleteStage"
                :edit-stage="editStage"
                :count="setCountCandidateByStatus(stageStatus)"
                @select="selectMultipleCandidatesAction(stagesMap[stageStatus], $event)"
                @input="editStage = $event"
                :is-group-selected="isStageSelected(stagesMap[stageStatus])"
              />
            </div>
            <VirtualWrapper
              v-for="candidate in filteredCandidates"
              :slot="candidate.id"
              :key="candidate.id"
              class="v-application v-application--wrap"
              min-height="148"
              transition="fade-transition"
            >
              <VCard
                outlined
                height="140"
                :class="{ selected: isCandidateSelected(candidate.id) }"
                hover
                :id="`candidate_card_${candidate.id}`"
                @click.native="showCandidate(candidate.id)"
                class="d-flex justify-space-between flex-column pa-2 ma-1 relative"
              >
                <div class="d-flex justify-space-between">
                  <div class="d-flex flex-column justify-space-between overflow-hidden">
                    <div class="candidate-card-title">
                      <div>{{ candidate.lastName }}</div>
                      <div>{{ candidate.firstName }}</div>
                    </div>
                    <div class="caption candidate-card-city">
                      <CandidateLocationAndYearsOld :candidate="candidate" />
                    </div>
                  </div>
                  <CandidateAvatar :candidate="candidate" :size="50" />
                </div>
                <div class="d-flex justify-lg-space-between align-center">
                  <CandidateRating :candidate="candidate" />
                  <span v-if="candidate.isNew" class="red--text text-lowercase caption new-candidate">
                    {{ $t('new') }}
                  </span>
                  <span v-if="isInterviewRecording(candidate)" class="interview-status">
                    <svg
                      width="20"
                      height="14"
                      viewBox="0 0 20 14"
                      fill="none"
                    >
                      <path
                        d="M19.012 2.146C18.386 1.873 17.66 1.992 17.161 2.452L15 4.452V3C15 1.346 13.654 0 12 0H3C1.346 0 0 1.346 0 3V11C0 12.655 1.346 14 3 14H12C13.654 14 15 12.655 15 11V9.549L17.161 11.548C17.481 11.845 17.896 12 18.319 12C18.553 12 18.788 11.953 19.012 11.855C19.621 11.589 20 11.02 20 10.371V3.63C20 2.981 19.621 2.412 19.012 2.146Z"
                        fill="#448AFF"
                      />
                    </svg>
                  </span>
                  <span v-if="candidate.selfRefused" class="red--text text-lowercase caption">
                    {{ $t('selfRefused') }}
                  </span>
                  <AppCheckbox
                    class="checkbox"
                    type="checkbox"
                    v-if="groupOperationsEnabled"
                    :value="isCandidateSelected(candidate.id)"
                    @input="selectCandidate(candidate.id, $event)"
                  />
                </div>
              </VCard>
            </VirtualWrapper>
          </kanban-board>
          <KanbanGroupOperations
            @enableOperationLoader="$emit('enableOperationLoader')"
            @disableOperationLoader="$emit('disableOperationLoader')"
            @messagesSent="(msg) => $emit('messagesSent', msg)"
            @getPaginatedCandidates="(payload) => $emit('getPaginatedCandidates', { ...payload, search })"
            @getCountCandidates="() => $emit('getCountCandidates', search)"
          />
        </div>
      </template>
      </div>
      <VacancyCandidatesKanbanStageDelete
        :stages="stages"
        v-if="deleteDialog && stages"
        v-model="deleteDialog"
        :deleted-stage="deletedStage"
      />
      <VacancyCandidatesDialogs
        v-if="(activeCandidate && !activeCandidate.selfRefused) || !activeCandidate"
        :vacancy="vacancy"
        :active-candidate="activeCandidate"
      />
      </div>
    </div>
    <div
      v-if="isExpired && !hideTariffOverlay"
      class="kanban-tariff-overlay"
    >
      <VCard class="kanban-tariff-card">
        <VCardTitle class="kanban-tariff-header">
          <img
            :src="require('@/assets/img/tarrif-change-warning.svg')"
            alt=""
            class="kanban-tariff-icon"
          >
          <div class="kanban-tariff-title">
            {{ $t('tariffInactiveTitle') }}
          </div>
        </VCardTitle>
        <VCardText class="kanban-tariff-text">
          {{ $t('tariffInactiveText') }}
        </VCardText>
        <VBtn
          class="kanban-tariff-btn"
          color="success"
          @click="redirectToUpgradePlan"
        >
          {{ $t('tariffInactiveButton') }}
        </VBtn>
      </VCard>
    </div>
  </div>
</template>

<script>
import VirtualWrapper from '@/components/VirtualWrapper';
import { makeid } from '@/functions';
import VacancyCandidatesKanbanStage from '@/components/Vacancy/VacancyCandidatesKanbanStage';
import {
  computed,
  onMounted,
  ref,
  toRefs,
  watch,
  onUnmounted,
} from '@vue/composition-api';
import useIntegrations from '@/compositions/useIntegrations';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import useKanban from '@/compositions/useKanban';
import VacancyCandidatesDialogs from '@/components/Vacancy/VacancyCandidatesDialogs';
import VacancyCandidatesKanbanStageDelete from '@/components/Vacancy/VacancyCandidatesKanbanStageDelete';
import VacancyCandidatesKanbanActions from '@/components/Vacancy/VacancyCandidatesKanbanActions';
import VacancyCandidatesKanbanSearch from '@/components/Vacancy/VacancyCandidatesKanbanSearch';
import useNotifications from '@/compositions/useNotifications';
import useKanbanPagination from '@/compositions/useKanbanPagination';
import KanbanGroupOperations from '@/components/Vacancy/Kanban/KanbanGroupOperations';
import { useKanbanMultipleOperations } from '@/compositions/useKanbanMultipleOperations';
import AppCheckbox from '@/components/AppCheckbox';
import CandidateRating from '../Candidate/CandidateRating.vue';
import CandidateAvatar from '../Candidate/CandidateAvatar.vue';
import CandidateLocationAndYearsOld from '../Candidate/CandidateLocationAndYearsOld.vue';
import Preloader from '../Preloader.vue';
import {
  candidateStatus,
  vacancyStatus,
  interviewStatusType,
  paymentTabs,
  settingsTabs,
} from '../../enums';
import useSubscription from '@/compositions/accounting/useSubscription';

export default {
  name: 'VacancyCandidatesKanban',
  components: {
    AppCheckbox,
    KanbanGroupOperations,
    VacancyCandidatesKanbanSearch,
    VacancyCandidatesKanbanActions,
    VacancyCandidatesKanbanStageDelete,
    VacancyCandidatesDialogs,
    VacancyCandidatesKanbanStage,
    CandidateLocationAndYearsOld,
    CandidateAvatar,
    Preloader,
    CandidateRating,
    VirtualWrapper,
  },
  props: {
    vacancy: {
      type: Object,
      required: true,
    },
    candidates: {
      type: Array,
      required: true,
    },
    countCandidatesByStatus: {
      type: Array,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    hideTariffOverlay: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { root, emit }) {
    const { useActions, useState } = createNamespacedHelpers('kanban');
    const { fetch, push, sort } = useActions(['fetch', 'push', 'sort']);
    const { stages } = useState(['stages']);
    const { isExpired } = useSubscription();
    const {
      vacancy, candidates, countCandidatesByStatus, hideTariffOverlay,
    } = toRefs(props);

    function redirectToUpgradePlan() {
      root.$router.push({
        name: 'settings',
        params: { type: settingsTabs.PAYMENT, addType: paymentTabs.INFO },
      });
    }
    const loading = ref(true);
    const searchLoading = ref(false);
    const {
      selectCandidate,
      toggleCandidate,
      operationsEnabled: groupOperationsEnabled,
      selectMultipleCandidates,
      isCandidateSelected,
    } = useKanbanMultipleOperations();

    const { hasNotifications } = useNotifications();

    const search = ref('');

    function isInterviewRecording(candidate) {
      return candidate?.interview?.status === interviewStatusType.RECORDING;
    }

    function setCountCandidateByStatus(stageStatus) {
      const stage = countCandidatesByStatus.value?.find((st) => st.status === stageStatus);
      return stage?.count || 0;
    }

    const filteredCandidates = computed(() => {
      return candidates.value;
    });

    const hasTest = computed(() => vacancy.value.hasTest);

    const prevScrollTop = ref(0);
    const hasTriggeredBottom = ref(false);
    const {
      currentPage,
      hasMoreData,
      isLoadingNextPage,
      reset: resetPagination,
      incrementPage,
    } = useKanbanPagination();
    resetPagination();

    watch(search, (newValue) => {
      const value = newValue || '';

      const innerLists = document.querySelectorAll('.drag-list');
      innerLists.forEach((list) => {
        list.scrollTop = 0;
      });

      searchLoading.value = true;
      emit('getCountCandidates', value);
      emit('getPaginatedCandidates', {
        page: 1,
        perPage: 20,
        search: value,
        onComplete: (hasData) => {
          searchLoading.value = false;
          hasMoreData.value = hasData !== false;
        },
      });
      resetPagination(value);
      hasTriggeredBottom.value = false;
    });

    fetch();

    const {
      updateBlock,
      activeCandidate,
      kanbanContainer,
      calculateColWidth,
      enableDrag,
    } = useKanban({ root, candidates, clear: true });

    const editStage = ref(null);
    const showHidden = ref(false);
    const isShowAllStages = ref(false);
    const deleteDialog = ref(false);
    const deletedStage = ref(null);

    const computedStages = computed(() => {
      const isVacancyHasExternalIntegration = computed(
        () => !!vacancy.value.externalVacancies?.length,
      );
      const { hasActiveIntegration } = useIntegrations();
      const hasManualInvitation = computed(() => {
        return !vacancy.value?.settings.autoInvitation;
      });
      const hasImportedCandidates = computed(() => {
        return candidates.value?.some((c) => c.status === candidateStatus.IMPORTED);
      });

      const hasImportedCandidatesInCounts = computed(() => {
        return countCandidatesByStatus.value?.some((c) => c.status === candidateStatus.IMPORTED);
      });

      const hasImportedStageInStages = computed(() => {
        return stages.value?.some((s) => s.status === candidateStatus.IMPORTED);
      });

      const isNeedImportStage = computed(() => {
        return (
          (hasActiveIntegration.value
            && isVacancyHasExternalIntegration.value
            && hasManualInvitation.value)
          || hasImportedCandidates.value
          || hasImportedCandidatesInCounts.value
          || hasImportedStageInStages.value
        );
      });

      if (isShowAllStages.value) {
        return stages.value;
      }

      let filtered = stages.value;

      if (!hasTest.value) {
        filtered = filtered.filter((s) => s.status !== candidateStatus.TESTED);
      }

      if (!isNeedImportStage.value) {
        filtered = filtered.filter((s) => s.status !== candidateStatus.IMPORTED);
      }

      return filtered;
    });

    const filteredStages = computed(() => {
      return showHidden.value
        ? computedStages.value
        : computedStages.value?.filter((s) => s.active || s.active === null || s.temp);
    });

    const stagesCodes = computed(() => {
      return filteredStages.value?.map((s) => s.status);
    });

    const isKanbanDataReady = computed(() => {
      return !!stagesCodes.value && !!filteredCandidates.value;
    });

    function countCandidates(status) {
      return candidates.value?.filter((it) => it.status === status).length;
    }

    const cssVars = ref();

    watch(
      hasNotifications,
      () => {
        cssVars.value = {
          ...cssVars.value,
          '--top-height': hasNotifications.value ? '53px' : '0px',
        };
      },
      { immediate: true },
    );

    function setColumnHeights() {
      const innerLists = document.querySelectorAll('.drag-inner-list');
      const columns = document.querySelectorAll('.drag-column');
      if (!innerLists.length || !columns.length) return;

      innerLists.forEach((list) => {
        list.style.height = 'auto';
      });
      columns.forEach((column) => {
        column.style.height = 'auto';
      });

      let maxHeight = 0;
      innerLists.forEach((list) => {
        const height = list.offsetHeight;
        if (height > maxHeight) {
          maxHeight = height;
        }
      });

      innerLists.forEach((list) => {
        list.style.height = `${maxHeight + 35}px`;
      });

      columns.forEach((column) => {
        column.style.height = `${maxHeight + 35}px`;
      });
    }

    function handleScroll(event) {
      const container = event.target;
      const { scrollTop } = container;
      const { clientHeight } = container;
      const { scrollHeight } = container;

      const scrollingDown = scrollTop > prevScrollTop.value;
      const atBottom = scrollTop + clientHeight >= scrollHeight - 50;

      if (scrollingDown && atBottom && !hasTriggeredBottom.value && hasMoreData.value && !isLoadingNextPage.value) {
        incrementPage();
        emit('getPaginatedCandidates', {
          page: currentPage.value,
          perPage: 20,
          search: search.value,
          onComplete: (hasData) => {
            hasTriggeredBottom.value = false;
            hasMoreData.value = hasData !== false;
            isLoadingNextPage.value = false;
          },
        });
        isLoadingNextPage.value = true;
        hasTriggeredBottom.value = true;
      }

      if (!atBottom) {
        hasTriggeredBottom.value = false;
      }

      prevScrollTop.value = scrollTop;
    }

    function initCols() {
      if (isExpired.value && hideTariffOverlay.value) return;
      const dragList = document.querySelector('.drag-list');
      if (!dragList) return;
      cssVars.value = {
        ...cssVars.value,
        '--col-width': `${calculateColWidth(computedStages.value.length)}px`,
      };
      enableDrag(stages, (map) => {
        sort(map);
      });
      setColumnHeights();

      const innerLists = document.querySelectorAll('.drag-list');
      innerLists.forEach((list) => {
        list.addEventListener('scroll', handleScroll);
      });
    }

    function showAllStages() {
      isShowAllStages.value = !isShowAllStages.value;
    }

    watch(stages, () => {
      setTimeout(() => {
        initCols();
        loading.value = false;
      }, 1);
    });

    watch([isExpired, hideTariffOverlay], () => {
      if (!isExpired.value || !hideTariffOverlay.value) {
        root.$nextTick(() => initCols());
      }
    });

    watch(filteredCandidates, () => {
      setTimeout(() => {
        setColumnHeights();
      }, 1);
    });

    watch(showHidden, () => {
      setTimeout(() => {
        setColumnHeights();
      }, 1);
    });

    watch(isShowAllStages, () => {
      setTimeout(() => {
        setColumnHeights();
      }, 1);
    });

    onMounted(() => {
      initCols();
      window.addEventListener('resize', setColumnHeights);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', setColumnHeights);
      const innerLists = document.querySelectorAll('.drag-list');
      innerLists.forEach((list) => {
        list.removeEventListener('scroll', handleScroll);
      });
    });

    function selectMultipleCandidatesAction(stage, value) {
      const selectedCandidates = filteredCandidates.value.filter(
        (candidate) => candidate.status === stage.status,
      );

      selectMultipleCandidates(
        selectedCandidates.map((candidate) => candidate.id),
        value,
      );
    }

    const isStageSelected = (stage) => {
      const selectedCandidates = filteredCandidates.value.filter(
        (candidate) => candidate.status === stage.status,
      );
      return selectedCandidates.some((candidate) => isCandidateSelected(candidate.id));
    };

    return {
      activeCandidate,
      search,
      filteredCandidates,
      stagesMap: computed(() => {
        return computedStages.value?.reduce((acc, s) => {
          acc[s.status] = s;
          return acc;
        }, {});
      }),
      editStage,
      showHidden,
      deleteDialog,
      isInterviewRecording,
      deletedStage,
      stages: computedStages,
      isCreateButtonsAvailable: computed(() => {
        return vacancy.value?.status === vacancyStatus.ACTIVE;
      }),
      kanbanConfig: computed(() => {
        return {
          accepts: () => {
            if (groupOperationsEnabled.value) {
              return false;
            }
            return (
              vacancy.value.status === vacancyStatus.ACTIVE && !root.$vuetify.breakpoint.mdAndDown
            );
          },
          moves: () => {
            if (groupOperationsEnabled.value) {
              return false;
            }
            return (
              vacancy.value.status === vacancyStatus.ACTIVE && !root.$vuetify.breakpoint.mdAndDown
            );
          },
        };
      }),
      stagesCounted: computed(() => {
        return computedStages.value?.reduce((acc, stage) => {
          acc[stage.status] = countCandidates(stage.status);
          return acc;
        }, {});
      }),
      addStage() {
        const tempStatus = makeid(10);
        push({
          name: '',
          status: tempStatus,
          id: tempStatus,
          temp: true,
        });
        editStage.value = tempStatus;
      },
      deleteStage(stage) {
        deleteDialog.value = true;
        deletedStage.value = stage;
      },
      showHiddenStages() {
        showHidden.value = !showHidden.value;
      },
      updateBlock: async (id, status) => {
        await updateBlock(id, status);
        emit('getCountCandidates', search.value);
        setTimeout(() => {
          setColumnHeights();
        }, 1);
      },
      showCandidate(candidateId) {
        if (groupOperationsEnabled.value) {
          toggleCandidate(candidateId);
          return;
        }
        root.$router.push({
          name: 'candidate',
          params: { candidateId },
        });
      },
      kanbanContainer,
      cssVars,
      loading,
      searchLoading,
      selectCandidate,
      selectMultipleCandidatesAction,
      isCandidateSelected,
      isStageSelected,
      groupOperationsEnabled,
      stagesCodes,
      setCountCandidateByStatus,
      isKanbanDataReady,
      isShowAllStages,
      showAllStages,
      isExpired,
      redirectToUpgradePlan,
    };
  },
};
</script>

<style lang="scss" scoped>
.kanban-page-root {
  position: relative;
}

.kanban-blurred-zone--blurred {
  filter: blur(4px);
}

.kanban-content-wrapper {
  position: relative;
}

.kanban-placeholder {
  min-height: 300px;
  width: 100%;
}

.kanban-tariff-overlay {
  position: absolute;
  top: -48px;
  left: -48px;
  right: -48px;
  bottom: -48px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  background: rgba(0, 0, 0, 0.55);
}

.kanban-tariff-card {
  max-width: 400px;
  width: 100%;
  padding: 30px;
  border-radius: 8px;
}

.kanban-tariff-header {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  padding: 0;
  margin-bottom: 8px;
}

.kanban-tariff-icon {
  padding: 13px;
  background: rgba(234, 13, 13, 0.1);
  border-radius: 50%;
  margin-bottom: 20px;
}

.kanban-tariff-title {
  font-weight: 400;
  font-size: 24px;
  line-height: 26px;
  color: #212121;
}

.kanban-tariff-text {
  padding: 0;
  margin-bottom: 24px;
  font-size: 14px;
  line-height: 19.6px;
  font-weight: 300;
  color: #212121;
}

.kanban-tariff-btn {
  width: 100%;
  font-weight: 400;
  font-size: 16px;
}
</style>

<style lang="scss">
.kanban-container {
  display: flex;
  width: 100%;
  overflow: hidden;
  position: relative;
  @media screen and(max-width: 768px) {
    overflow: auto;
  }
}

.kanban-header {
  align-items: center;
  @media screen and (max-width: 1065px) {
    margin-bottom: 10px;
    align-items: normal;
  }
}

.kanban-scope {
  width: max-content;
  position: relative;
  min-height: 100%;
  height: auto;
  margin-right: -2px;
  width: 100%;
  @media screen and (max-width: 599px) {
    width: 100%;
  }
}

.drag-container {
  min-height: 100%;
  height: auto;
  display: inline-block;
  vertical-align: top;
  width: 100%;
}

$kanban-ease-out: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);

ul.drag-list,
ul.drag-inner-list {
  list-style-type: none;
  padding: 0;
}

.drag-inner-list {
  background: #f9f9f9;
  border-radius: 0.25rem;
  position: relative;

  &:before {
    content: ' ';
    position: absolute;
    top: 11.8px;
    left: 50%;
    width: calc(100% - 8px);
    min-width: calc(100% - 8px);
    height: 140px;
    min-height: 140px;
    border-radius: 5px;
    border: 1px dashed #c9c9c9;
    transform: translateX(-50%);
  }

  @media screen and (max-width: 768px) {
    overflow: auto;
  }
}

.drag-list {
  margin: 0;
  display: flex;
  min-height: 100%;
  overflow: auto;
  height: calc(100vh - 250px - var(--top-height));
  @media screen and (max-width: 1263px) {
    height: calc(100vh - 300px - var(--top-height));
  }
  @media screen and (max-width: 1065px) {
    height: calc(100vh - 310px - var(--top-height));
  }
  @media screen and (max-width: 959px) {
    height: calc(100vh - 355px - var(--top-height));
  }
  @media screen and (max-width: 800px) {
    height: calc(100vh - 390px - var(--top-height));
  }
  @media screen and (max-width: 768px) {
    height: 100%;
    flex-direction: column;
  }
}
.drag-options {
  display: none;
}

@media #{map-get($display-breakpoints, 'md-and-down')} {
  .drag-column {
    flex-basis: 190px;
  }
}

.drag-column {
  display: inline-flex;
  flex-direction: column;
  flex: 1;
  position: relative;
  overflow: visible;
  min-width: 200px;
  margin: 0 0 0.5rem 0.5rem;
  &:first-child {
    margin-left: 0;
  }
  @media screen and (max-width: 768px) {
    width: 100%;
    margin: 0 0 0.5rem 0;
    min-height: 184px;
    max-height: 320px;
  }
}

.header-fixed {
  @media screen and (min-width: 1240px) {
    position: fixed !important;
    top: 0 !important;
    width: 100%;
  }
  @media screen and (min-width: 1440px) {
    max-width: 235px;
  }
}

.drag-column-header {
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 2.875rem;
  font-size: 0.8125rem;
  font-weight: bold;
  border: 2px solid #f9f9f9;
  background: white;
  border-radius: 0.25rem;
  &:after {
    content: '';
    position: absolute;
    height: 0.5rem;
    bottom: -0.625rem;
    left: -2px;
    right: -2px;
    background: white;
  }
}
.drag-header-text {
  padding: 0 2rem 0 0.375rem;
}
.gu-mirror .drag-header-text {
  font-size: 0.8125rem;
  font-family: 'MuseoSansCyrl', sans-serif;
}

.drag-item {
  height: 8.8rem;
  transition: $kanban-ease-out;
  margin-top: 0.5rem;
  &:last-child {
    margin-bottom: 0.75rem;
  }
  &.is-moving {
    transform: scale(1.1);
  }
}

.drag-header-more {
  cursor: pointer;
}

.drag-options {
  position: absolute;
  top: 44px;
  left: 0;
  width: 100%;
  height: auto;
  padding: 10px;
  transform: translateX(100%);
  opacity: 0;
  transition: $kanban-ease-out;

  &.active {
    transform: translateX(0);
    opacity: 1;
  }

  &-label {
    display: block;
    margin: 0 0 5px 0;

    input {
      opacity: 0.6;
    }

    span {
      display: inline-block;
      font-size: 0.9rem;
      font-weight: 400;
      margin-left: 5px;
    }
  }
}

/* Dragula CSS  */

.gu-mirror {
  position: fixed !important;
  margin: 0 !important;
  z-index: 9999 !important;
  opacity: 0.8;
  list-style-type: none;
}

.gu-hide {
  display: none !important;
}

.gu-unselectable {
  -webkit-user-select: none !important;
  -moz-user-select: none !important;
  -ms-user-select: none !important;
  user-select: none !important;
}

.gu-transit {
  opacity: 0.2;
}

.candidate--block {
  border: unset;
  transition: border 0.2s ease-in-out;
}

.selected {
  border: 2px solid $success !important;
}

.v-tabs-bar {
  @media screen and (max-width: 768px) {
    margin-bottom: 20px !important;
  }
  @media screen and (max-width: 428px) {
    margin-bottom: 0px !important;
  }
}

.new-candidate {
  position: absolute;
  top: 50%;
  right: 15px;
}

.sortable-chosen .drag-inner-list {
  visibility: hidden !important;
}

.sortable-chosen .drag-column-header:after {
  display: none;
}

.search-loader {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  transform: translate(-50%, -50%);
  z-index: 1000;
  background: rgba(255, 255, 255, 0.9);
  padding: 20px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
</style>

<style scoped lang="scss">
.v-stepper {
  box-shadow: none;
}

.v-stepper__content {
  padding: 0;
}

.candidate-card-title {
  font-size: 0.875rem;
  line-height: 1rem;
  max-height: 90px;
  max-width: 133px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
}

.candidate-card-city {
  margin-top: 5px;
  min-width: 100px;
}

.checkbox {
  position: absolute;
  bottom: 12px;
  right: 12px;
}

.interview-status {
  position: absolute;
  right: 7px;
  bottom: 30px;
}
</style>

<i18n locale="en" lang="yaml">
invited: Invitation sent
interviewed: Passed the interview
inoffice_interview: Invited for an in-office interview
job_offer: Job offer
hired: Hired
declined: Declined
noCandidates: There are no candidates for this job yet.
inviteCandidate: Invite candidate
search: Search
recordOfficeInterview: To record an in-person interview
new: New
selfRefused: refused
tariffInactiveTitle: Your tariff is inactive.
tariffInactiveText: To gain access to all the functions of your tariff, you need to make a payment.
tariffInactiveButton: Upgrade plan
</i18n>

<i18n locale="ru" lang="yaml">
invited: Приглашено
interviewed: Прошли интервью
inoffice_interview: Приглашен на очное интервью
job_offer: Предложение о работе
hired: Выход на работу
declined: Отказ
noCandidates: Соискателей по данной вакансии пока нет.
search: Поиск
recordOfficeInterview: Записать очное интервью
new: новое
selfRefused: отказался
tariffInactiveTitle: Тариф закончился
tariffInactiveText: Для получения доступа к функциям Вашего тарифа необходимо внести оплату.
tariffInactiveButton: Оплатить тариф
</i18n>
